import { z } from 'zod';

/**
 * End-user feedback pulled from lock providers, and the cross-channel contact
 * history built from it plus support tickets.
 *
 * Zod enums rather than TypeScript enums, per the project convention: one
 * declaration gives both runtime validation and the inferred union type.
 * Values mirror the offerings-service enums exactly — drift here silently
 * breaks filtering, since the API rejects unknown values with a 422.
 *
 * Nothing in this file names a provider. `providerName` is an open enum on
 * purpose (see below) so a new adapter reaches the portal without a frontend
 * release.
 */

export const FeedbackTriageStatusSchema = z.enum([
  'new',
  'in_review',
  'resolved',
]);

/**
 * Providers, as a plain string rather than a z.enum.
 *
 * The whole point of the capability-driven design is that adding Knox is an
 * adapter plus a flag. A closed enum here would make every new provider a
 * frontend change too — and worse, would fail *validation* on rows the
 * backend considers perfectly valid, hiding real feedback behind a parse
 * error. Display falls back to the raw value.
 */
export const ProviderNameSchema = z.string();

export const ContactChannelSchema = z.enum([
  'provider_feedback',
  'support_ticket',
]);

export const CustomerFeedbackSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  providerName: ProviderNameSchema,
  submittedAt: z.string(),
  ingestedAt: z.string(),

  providerDeviceTag: z.string().nullable(),
  imei: z.string().nullable(),
  providerOrderNum: z.string().nullable(),

  /** Null when the feedback matched no enrolled device — see the Unmatched filter. */
  deviceUnitId: z.string().nullable(),
  contractId: z.string().nullable(),
  customerId: z.string().nullable(),

  /** The vendor's own string. Undocumented value set, so never mapped to an enum. */
  feedbackType: z.string().nullable(),
  description: z.string().nullable(),
  contactPhone: z.string().nullable(),
  contactEmail: z.string().nullable(),
  attachmentUrl: z.string().nullable(),

  triageStatus: FeedbackTriageStatusSchema,
  triageNote: z.string().nullable(),
  triagedByUserId: z.string().nullable(),
  triagedByName: z.string().nullable(),
  triagedAt: z.string().nullable(),

  // Denormalised display context, resolved server-side.
  customerName: z.string().nullable(),
  customerPhone: z.string().nullable(),
  deviceName: z.string().nullable(),
  deviceImei: z.string().nullable(),

  createdAt: z.string(),
  lastModifiedAt: z.string(),
});

export const FeedbackStatsSchema = z.object({
  new: z.number(),
  inReview: z.number(),
  resolved: z.number(),
  unmatched: z.number(),
  total: z.number(),
});

export const ContactHistoryEntrySchema = z.object({
  id: z.string(),
  channel: ContactChannelSchema,
  occurredAt: z.string(),
  title: z.string().nullable(),
  body: z.string().nullable(),
  /** Each channel's own raw status value — the label lives in the frontend. */
  status: z.string(),
  providerName: ProviderNameSchema.nullable(),
  deviceUnitId: z.string().nullable(),
  contractId: z.string().nullable(),
  attachmentUrl: z.string().nullable(),
  reference: z.string().nullable(),
});

export const FeedbackRefreshResultSchema = z.object({
  fetched: z.number(),
  inserted: z.number(),
  attributed: z.number(),
  reresolved: z.number(),
  providersPolled: z.array(z.string()),
  errors: z.array(z.string()),
});

// ── Request schemas, derived from the base per the project convention ───────

export const UpdateFeedbackTriageSchema = CustomerFeedbackSchema.pick({
  triageStatus: true,
  triageNote: true,
}).partial();

export const FeedbackFiltersSchema = z.object({
  providerName: z.string().optional(),
  feedbackType: z.string().optional(),
  triageStatus: FeedbackTriageStatusSchema.optional(),
  deviceUnitId: z.string().optional(),
  imei: z.string().optional(),
  customerId: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  /** Rows matching no device. Superuser-only; the API refuses it otherwise. */
  unmatched: z.boolean().optional(),
  search: z.string().optional(),
  skip: z.number().optional(),
  limit: z.number().optional(),
});

export const ContactHistoryFiltersSchema = z.object({
  customerId: z.string().optional(),
  contractId: z.string().optional(),
  skip: z.number().optional(),
  limit: z.number().optional(),
});

export type FeedbackTriageStatus = z.infer<typeof FeedbackTriageStatusSchema>;
export type ContactChannel = z.infer<typeof ContactChannelSchema>;
export type CustomerFeedback = z.infer<typeof CustomerFeedbackSchema>;
export type FeedbackStats = z.infer<typeof FeedbackStatsSchema>;
export type ContactHistoryEntry = z.infer<typeof ContactHistoryEntrySchema>;
export type FeedbackRefreshResult = z.infer<typeof FeedbackRefreshResultSchema>;
export type UpdateFeedbackTriage = z.infer<typeof UpdateFeedbackTriageSchema>;
export type FeedbackFilters = z.infer<typeof FeedbackFiltersSchema>;
export type ContactHistoryFilters = z.infer<typeof ContactHistoryFiltersSchema>;

// ── Display metadata ───────────────────────────────────────────────────────

/** i18n key suffixes, resolved under `feedback.triage.*`. */
export const TRIAGE_STATUS_KEYS: Record<FeedbackTriageStatus, string> = {
  new: 'feedback.triage.new',
  in_review: 'feedback.triage.inReview',
  resolved: 'feedback.triage.resolved',
};

export const CHANNEL_KEYS: Record<ContactChannel, string> = {
  provider_feedback: 'feedback.channel.providerFeedback',
  support_ticket: 'feedback.channel.supportTicket',
};

export const TRIAGE_ORDER: readonly FeedbackTriageStatus[] = [
  'new',
  'in_review',
  'resolved',
] as const;

/**
 * Turn a provider key into something displayable without a lookup table.
 *
 * A hard-coded map would mean a provider we have not shipped a frontend for
 * renders as blank. Title-casing the key is never wrong, only occasionally
 * inelegant — and it keeps this file free of provider names.
 */
export function providerLabel(name: string | null | undefined): string {
  if (!name) return '—';
  return name.charAt(0).toUpperCase() + name.slice(1);
}
